/**
 * src/game/progression/meta-shop.ts — 永久升級商店(PowerUp / meta-shop)資料驅動 catalog
 * + 套用層(純函式).
 *
 * Spec: docs/spec/LONG_TERM_PROGRESSION.md §2(永久 meta 商店)/ §2.3(遞減 + 天花板)/ §7(落地).
 *
 * 雙軌進度鐵則 (CLAUDE.md 最高優先 / commander.ts NEVER 規範同款):
 *   - 本模組是 **玩遊戲軌**。**NEVER** 讀/寫 factionXP / freeXP / skillLevels /
 *     傷害公式 / 修煉值。它只讀 PlayerProgress 的 **遊戲軌欄位** metaShop / metaCoins,
 *     回傳「該英雄的永久乘數」(傷害/HP/移速/幸運…),每項硬封頂。
 *   - 學習獎勵是另一份 spec;這裡只留 `learningBonus` 鉤(預設 0、不實作)。
 *
 * 設計(§2):
 *   - **per-英雄(5 門派)各一棵小樹**(每樹 4-6 節點,主題化)。
 *   - 數值節點走 **VS 實證上限**:傷害 +5%/級 ×5 = +25%(META_DAMAGE_CAP);
 *     最大 HP ×1.1/級 ×3 ≈ +33%(META_HP_CAP);移速/幸運等每項 3-5 級、各自硬上限。
 *   - 全滿也只是「底盤微抬」(差異性與爆發來自單場 build,§5)。
 *
 * 純函式、無 Phaser、無 I/O。caller(run 起手 / 商店 UI)負責讀寫 PlayerProgress + saveProgress。
 */
import type { FactionId, PlayerProgress } from '../../shared/types';

// ── 全滿硬天花板(§2.3,VS 實證,**不可超過**)──────────────────────────────
//
// VS PowerUp 實證:Might +5%/級×5 = +25% 傷害;Max Health ×1.1/級×3 ≈ +33%(乘法 1.331)。
// applyMetaBonuses 對每項乘數做 Math.min(cap, …) 硬夾,守門測試斷言「全滿 ≤ cap」。

/** 永久傷害乘數上限(全滿 = +25%,= 1.25)。VS Might 原值。 */
export const META_DAMAGE_CAP = 1.25;
/** 永久最大 HP 乘數上限(全滿 ≈ +33%,= 1.33)。VS Max Health 原值(1.1^3≈1.331)。 */
export const META_HP_CAP = 1.33;
/** 永久移速乘數上限(全滿 = +15%)。每項各自小幅、硬上限(§2.3)。 */
export const META_SPEED_CAP = 1.15;
/** 永久幸運(掉落/金幣)乘數上限(全滿 = +20%)。 */
export const META_LUCK_CAP = 1.2;

/** meta 乘數種類(對齊 §2.3「數值底盤」項目;每項各自硬封頂)。 */
export type MetaStatKind = 'damage' | 'maxHp' | 'moveSpeed' | 'luck';

/** 每種 stat 的硬天花板(全滿 clamp 值)。守門測試直接讀此表斷言。 */
export const META_STAT_CAP: Readonly<Record<MetaStatKind, number>> = {
  damage: META_DAMAGE_CAP,
  maxHp: META_HP_CAP,
  moveSpeed: META_SPEED_CAP,
  luck: META_LUCK_CAP,
};

/** applyMetaBonuses 回傳:該英雄的永久乘數(每項 1 = 無加成,已夾在各自 cap 內)。 */
export interface MetaBonuses {
  /** 傷害乘數(套到 base bullet damage;cap META_DAMAGE_CAP)。 */
  damageMult: number;
  /** 最大 HP 乘數(套到 initial_hp;cap META_HP_CAP)。 */
  hpMult: number;
  /** 移速乘數(cap META_SPEED_CAP)。 */
  moveSpeedMult: number;
  /** 幸運乘數(掉落/金幣;cap META_LUCK_CAP)。 */
  luckMult: number;
}

/** 全 1(無任何 meta 加成)的中性 MetaBonuses,run 起手 fallback 用。 */
export const NEUTRAL_META_BONUSES: Readonly<MetaBonuses> = {
  damageMult: 1, hpMult: 1, moveSpeedMult: 1, luckMult: 1,
};

// ── catalog 節點型別 ───────────────────────────────────────────────────────

/** 數值底盤節點:每級給 `perLevel` 的「加成」,maxLevel 級,套用時受 stat cap 夾住。 */
export interface MetaStatNode {
  /** 節點 id(該英雄樹內唯一)。 */
  id: string;
  /** UI 顯示名(遊戲化用詞,不出現學科名)。 */
  label: string;
  /** 影響哪個 stat。 */
  stat: MetaStatKind;
  /** 每級加成:damage/maxHp/moveSpeed/luck 都解讀為「+perLevel 的倍率」
   *  (例:0.05 = 每級 +5%)。多級線性累加後再夾 cap。 */
  perLevel: number;
  /** 最大級數(§2.3:級數少)。傷害類設 5、HP 類設 3,其餘 3-5。 */
  maxLevel: number;
  /** 該節點第 1 級成本(指數成本 base;見 meta-economy.metaNodeCostAtLevel)。 */
  cost1: number;
}

/** 一個英雄(門派)的 meta 小樹。 */
export interface MetaHeroTree {
  heroId: FactionId;
  /** 樹主題短語(UI 副標;主題化見 §2.2)。 */
  theme: string;
  /** 4-6 個數值節點。 */
  nodes: MetaStatNode[];
}

// ── catalog:per-英雄(5 門派)各一棵小樹(§2.2 主題化)──────────────────────
//
// 數值一律照 §2.3 VS 實證上限,不自己發明:
//   傷害類節點:+5%/級 × 5 級 = +25%(= META_DAMAGE_CAP,單節點即可達上限)。
//   HP 類節點:+11%/級 × 3 級 ≈ +33%(= META_HP_CAP)。
//   移速/幸運:每項 3-4 級、每級小,各自硬上限。
// 主題化:機械=移速/偵察、星算=傷害/暴擊、符文=HP/防禦、自然=持續/幸運、疆域=資源/全面。
// cost1 對齊 VS(初始 ~200);指數成長率在 meta-economy.META_COST_GROWTH。

const DAMAGE_PER_LEVEL = 0.05;  // +5%/級(VS Might)
const DAMAGE_MAX_LEVEL = 5;     // ×5 = +25% 上限
const HP_PER_LEVEL = 0.11;      // +11%/級(≈ VS ×1.1)
const HP_MAX_LEVEL = 3;         // ×3 ≈ +33% 上限
const SPEED_PER_LEVEL = 0.05;   // +5%/級
const SPEED_MAX_LEVEL = 3;      // +15% 上限
const LUCK_PER_LEVEL = 0.05;    // +5%/級
const LUCK_MAX_LEVEL = 4;       // +20% 上限

/** 數值底盤節點工廠(統一帶 VS 實證 perLevel/maxLevel,避免各處手抄)。 */
function statNode(id: string, label: string, stat: MetaStatKind, cost1: number): MetaStatNode {
  switch (stat) {
    case 'damage':    return { id, label, stat, perLevel: DAMAGE_PER_LEVEL, maxLevel: DAMAGE_MAX_LEVEL, cost1 };
    case 'maxHp':     return { id, label, stat, perLevel: HP_PER_LEVEL,     maxLevel: HP_MAX_LEVEL,     cost1 };
    case 'moveSpeed': return { id, label, stat, perLevel: SPEED_PER_LEVEL,  maxLevel: SPEED_MAX_LEVEL,  cost1 };
    case 'luck':      return { id, label, stat, perLevel: LUCK_PER_LEVEL,   maxLevel: LUCK_MAX_LEVEL,   cost1 };
  }
}

/** 5 門派 meta 小樹 catalog(§2.2)。每樹 4-5 節點,主題化,各派偏重不同 stat。 */
export const META_HERO_TREES: Readonly<Record<FactionId, MetaHeroTree>> = {
  // ⚡ 星算門:精準爆發 — 偏傷害,輔以一點幸運。
  celestial: {
    heroId: 'celestial', theme: '精準爆發',
    nodes: [
      statNode('cel-might', '星算·威能', 'damage', 200),
      statNode('cel-focus', '星算·凝聚', 'damage', 240),
      statNode('cel-vigor', '星算·體魄', 'maxHp', 220),
      statNode('cel-fortune', '星算·星運', 'luck', 180),
    ],
  },
  // 🔮 符文門:防禦控制 — 偏 HP,輔以移速。
  runic: {
    heroId: 'runic', theme: '防禦控制',
    nodes: [
      statNode('run-ward', '符文·守護', 'maxHp', 200),
      statNode('run-bulwark', '符文·壁壘', 'maxHp', 260),
      statNode('run-might', '符文·咒能', 'damage', 240),
      statNode('run-step', '符文·靈步', 'moveSpeed', 180),
    ],
  },
  // 🤖 機械門:速度偵察 — 偏移速/幸運(偵察=多撿),輔以一點傷害。
  techno: {
    heroId: 'techno', theme: '速度偵察',
    nodes: [
      statNode('tec-thruster', '機械·推進', 'moveSpeed', 180),
      statNode('tec-overdrive', '機械·超載', 'moveSpeed', 240),
      statNode('tec-scanner', '機械·偵蒐', 'luck', 200),
      statNode('tec-railgun', '機械·軌道砲', 'damage', 240),
    ],
  },
  // 🌀 自然門:元素持續 — 偏幸運(持續=滾雪球)+ HP。
  elemental: {
    heroId: 'elemental', theme: '元素持續',
    nodes: [
      statNode('ele-bloom', '自然·繁茂', 'luck', 180),
      statNode('ele-overgrow', '自然·蔓生', 'luck', 240),
      statNode('ele-bark', '自然·樹皮', 'maxHp', 220),
      statNode('ele-spark', '自然·觸電', 'damage', 240),
    ],
  },
  // 🏰 疆域門:資源支援 — 全面均衡(傷害/HP/移速/幸運各一,當「樣樣通」主玩英雄)。
  dominion: {
    heroId: 'dominion', theme: '資源支援',
    nodes: [
      statNode('dom-levy', '疆域·徵召', 'damage', 220),
      statNode('dom-rampart', '疆域·城防', 'maxHp', 220),
      statNode('dom-march', '疆域·行軍', 'moveSpeed', 200),
      statNode('dom-tithe', '疆域·稅收', 'luck', 200),
    ],
  },
};

/** 取某英雄的 meta 樹(未知 heroId → celestial,永不回 undefined)。 */
export function metaTreeFor(heroId: FactionId): MetaHeroTree {
  return META_HERO_TREES[heroId] ?? META_HERO_TREES.celestial;
}

// ── 等級讀寫 helpers(只碰遊戲軌 metaShop,絕不碰學習軌)─────────────────────

/** 某英雄某節點目前等級(metaShop[heroId][nodeId];缺省 0)。 */
export function metaNodeLevel(p: PlayerProgress, heroId: FactionId, nodeId: string): number {
  const tree = p.metaShop?.[heroId];
  const lv = tree?.[nodeId];
  return Number.isFinite(lv) && (lv as number) > 0 ? Math.floor(lv as number) : 0;
}

/**
 * 冪等設定某節點等級(夾在 [0, maxLevel])。mutate-in-place;caller 負責 saveProgress。
 * 只動 metaShop(遊戲軌),絕不碰 factionXP/skillLevels。回傳實際寫入的等級。
 */
export function setMetaNodeLevel(p: PlayerProgress, heroId: FactionId, nodeId: string, level: number): number {
  const node = metaTreeFor(heroId).nodes.find((n) => n.id === nodeId);
  const maxLevel = node ? node.maxLevel : 0;
  const lv = Math.max(0, Math.min(maxLevel, Number.isFinite(level) ? Math.floor(level) : 0));
  if (!p.metaShop || typeof p.metaShop !== 'object') p.metaShop = {};
  if (!p.metaShop[heroId] || typeof p.metaShop[heroId] !== 'object') p.metaShop[heroId] = {};
  p.metaShop[heroId][nodeId] = lv;
  return lv;
}

// ── 套用層:applyMetaBonuses(§2.3 每項硬封頂)────────────────────────────────

/**
 * 回該英雄的永久乘數(傷害/HP/移速/幸運),**每項硬封頂**(超過 cap 夾住)。
 *
 * 鐵則:**只讀遊戲軌欄位 metaShop**,絕不讀/寫 factionXP/skillLevels/修煉值。
 * 同 stat 的多個節點線性累加後,套 `Math.min(STAT_CAP, 1 + Σ(perLevel × level))`,
 * 確保「全滿傷害 ≤ +25% / HP ≤ +33%」(§2.3,守門測試斷言)。
 *
 * 用法(run 起手):applyMetaBonuses(progress, heroId).damageMult 乘到 base bullet damage、
 * hpMult 乘到 initial_hp(scene 各自再 clamp 在自己的 run cap 內,各自封頂)。
 */
export function applyMetaBonuses(p: PlayerProgress, heroId: FactionId): MetaBonuses {
  const tree = metaTreeFor(heroId);
  // 各 stat 的累加加成(Σ perLevel × level),之後 +1 再夾 cap。
  const sum: Record<MetaStatKind, number> = { damage: 0, maxHp: 0, moveSpeed: 0, luck: 0 };
  for (const node of tree.nodes) {
    const lv = metaNodeLevel(p, heroId, node.id);
    if (lv <= 0) continue;
    sum[node.stat] += node.perLevel * lv;
  }
  const clamp = (kind: MetaStatKind): number =>
    Math.min(META_STAT_CAP[kind], Math.max(1, 1 + sum[kind]));
  return {
    damageMult:    clamp('damage'),
    hpMult:        clamp('maxHp'),
    moveSpeedMult: clamp('moveSpeed'),
    luckMult:      clamp('luck'),
  };
}

// ── 學習層接縫(§6,只留位,預設 0、不實作)────────────────────────────────

/**
 * 學習軌加成鉤(LONG_TERM_PROGRESSION §6:learningBonus 預設 0)。
 * 學習 spec(docs/spec/LEARNING_REWARDS.md,未定)之後填;**本模組永遠回 0**,
 * 不讀 factionXP/skillLevels(維持雙軌物理隔離)。caller 把它當
 * 「玩家最大 DPS 估計 = runBuild × metaCeiling × (1 + learningBonus)」的第三項。
 */
export function learningBonus(_p: PlayerProgress): number {
  return 0;
}
